import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import SpineTextureExtractor from "../../../utils/SpineTextureExtractor";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import { Settings } from "../../../settings";
import { FONT_STYLES, scaleStyle } from "../../../fontStyles";
import {
  getLabelTexture,
  warmLabelTextures,
} from "../../Shared/textLabelCache";
import { SOUND_NAME } from "../../../sounds";
import { toStaticCell as gambleStaticCell } from "../GambleScatter/gambleScatterConfig";
import {
  symbolData,
  BONUS_SYMBOL_ID,
  COIN_SYMBOL_IDS,
  COLLECT_SYMBOL_ID,
  MULTIPLIER_SYMBOL_ID,
  MULTIPLIER_VALUE_TO_SKIN,
  SCATTER_1_SYMBOL_ID,
  SCATTER_2_SYMBOL_ID,
  SCATTER_3_SYMBOL_ID,
} from "../../../SymbolData";

/**
 * Golden Mystery (B2) — the track that spins inside the cell.
 *
 * The host chain is two spines deep and NESTED — each lives in the window of the
 * one above, because that is what a window is for:
 *
 *   `my`   the mystery frame. Its doors open with `reveal_spin` — the variant
 *          that does NOT fade the frame, the backing or the twelve lights — and
 *          then `anticipation_spin` loops its light chase for as long as the
 *          track runs. Both clips were dropped by the 2026-08-21 re-export and
 *          restored by hand; see `frame.open` / `frame.spinning` below.
 *   `myg`  INSIDE the mystery's `attachment_slot_symbol`, so the doors open onto
 *          it. `reveal` brings the golden sign to life with an energy burst and
 *          a boom, then removes itself: `golden_myst` fades at 0.767, its
 *          backing at 1.0, and its OWN `attachment_slot_symbol` is keyed UP to
 *          full alpha at exactly t=1. The clip hands the cell over on its last
 *          frame.
 *
 * So the track lives in `myg`'s slot — the one whose alpha that clip drives —
 * and the golden symbol is the thing that hides it until the boom. Put `myg`
 * beside the frame instead of inside it and it covers the whole cell, floating
 * over the doors rather than waiting behind them.
 *
 * `myg.reveal_spin` is deliberately unused. It is the same clip plus a fade-in
 * of myg's OWN backing and frame, which is for a host standing alone; we have
 * the mystery's frame underneath and do not want a second one.
 *
 * Strip cells are FLAT TEXTURES, the same choice the gamble scatter's strip
 * makes and for the same reason: a skeleton per cell is five ticking spines for
 * something the eye only sees as blur.
 */

const COIN_SYMBOL_ID = COIN_SYMBOL_IDS[0];

// ── Where the value sits on a coin, and how big ──
//
// The `c` rig carries a text slot per skin — txt_slot_coin1..4 — and all four
// hang off ONE bone. Walking it root-ward:
//
//   root -> scale (1.3) -> coins_main -> coins -> value (y 0.13)
//
// So the number is dead centre of the coin — the offset is 0.13 of a skeleton
// unit, which is as good as nothing, and it is kept only because reproducing the
// bone honestly costs one multiply.
//
// The 1.3 that bone would pass down is deliberately NOT applied. It matters for
// the gamble card's number, whose font size is authored in skeleton units (see
// FS_NUMBER_BONE_SCALE); this size is computed in screen pixels from the cell,
// so applying the bone scale as well would simply make it 30% too big.
//
// Y is negated for PIXI, which is Y-down.
const COIN_VALUE_OFFSET_Y = 0.13;

// ── The value's size comes from the COIN, not from the style ──
//
// The four styles arrived at fontSize 13, which is the size the number was
// authored at inside the Spine project — meaningless out here, where a cell is
// whatever the viewport makes it. So the size is derived the way every other
// coin value in this engine is derived, in ReelControllerV2:
//
//   fontSize = cellHeight x multiplier
//
// That is the rule, so the number grows and shrinks with the coin on every
// resize. The multiplier was the shared 0.25 until 2026-09-24 and is now the
// Golden cell's own GOLDEN_VALUE_FONT_SIZE, below.
//
// Everything else in the styles is kept exactly as supplied: the fills, the
// strokes, the shadow colours and distances are the artwork's, and only the size
// is ours.
//
// ── Then it is made to FIT ──
//
// x1 and x5000 are the same cell. A four-digit value at that height overflows
// the coin's face, so the same fit-to-width rule the reels use
// squeezes it back inside: 12% padding each side, and only the X axis is
// squeezed up to four digits, because a coin reads better slightly narrow than
// small. Beyond that both axes go down together.
const COIN_VALUE_PADDING = 0.12;

// How far the number may be squeezed on X alone before both axes go down
// together. Narrowing distorts the stroke and the shadow, and there is a point
// past which that is more noticeable than a smaller number.
//
// 0.7, was 0.8: with the bigger font below, four and five digits need more of
// it, and a slightly narrower number still reads bigger than a smaller one.
const COIN_VALUE_MIN_SQUEEZE = 0.7;

// ── The Golden cell's OWN font size, as a fraction of the cell height ──
//
// Was Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER (0.25), which is SHARED — the
// reel coin texts, Pot Collection and Royal Collect all read it — so raising it
// here would have resized all of them. Feedback 2026-09-24: the numbers in the
// coins are too small. Vladimir: *"we still have to fit the coin but lets make
// it bigger however you can."*
//
// The fit above still holds: COIN_VALUE_PADDING is unchanged, so however big
// this is, no number is wider than the coin's face. Short values (1 to 500)
// take the whole increase; long ones take it in height and are narrowed on X.
//
// ONE value for every site that sizes a golden number — renderCell, the spine's
// worn number and warmGoldenLabels. The label cache is keyed on the size, so
// the three must agree or the warm-up builds labels nothing ever uses.
const GOLDEN_VALUE_FONT_SIZE = 0.32;

/**
 * How much fatter the BACKING glyph's outline is than the real one.
 *
 * A stroke is drawn centred on the letter's outline, so it eats into a digit's
 * counter — the hole in a 6, 0, 8, 9, 4 — from every side. The collector's own
 * weight very nearly closes that hole, and what shows through the gap left over
 * is the artwork behind: a dark dot in the middle of the number.
 *
 * It only has to cover that last gap, so this wants to be as small as it can be:
 * a centred stroke grows the OUTER silhouette by half its thickness too, and a
 * backing far fatter than the front one shows as a heavier cream edge around the
 * digit. 1.7 still left a dot on the roundest ones; 2.2 closes them.
 *
 * Otherwise drawn entirely BEHIND the real glyph, at its own weight on top.
 */
const COLLECTOR_VALUE_BACKING_STROKE = 2.2;

/**
 * The coin's visual tier, from its value.
 *
 * GDD §3.4 gives four tiers of three values each, and the `c` spine carries the
 * matching skins coin1-coin4:
 *
 *   x1    x2    x5      bronze       coin4
 *   x10   x25   x50     silver       coin3
 *   x100  x250  x500    dark gold    coin2
 *   x1000 x2500 x5000   bright gold  coin1
 *
 * ── The RIG IS NUMBERED THE OTHER WAY ROUND ──
 *
 * This read `coin1` for the lowest tier and `coin4` for the highest, which is
 * the order the names suggest and the order the GDD lists the tiers in. The art
 * does the opposite: measured off `c.webp`, coin1 is bright gold (hue 45, sat
 * 0.96) and coin4 is bronze (hue 25), with silver at coin3. So every coin on
 * the board wore the wrong tier — a x1 came up gold and a x5000 came up bronze.
 * Reported by the team, 2026-09-09.
 *
 * Do not "tidy" this back into ascending order without re-measuring the atlas.
 * The names are the animator's numbering, not a ranking.
 *
 * The text slot follows the skin — `txt_slot_${coinSkinForValue(...)}` — so it
 * is this one function that keeps art and number together. Both move as one.
 *
 * Read as thresholds rather than a lookup table, so a value the math produces
 * that the GDD does not list still lands in the right tier instead of nowhere.
 */
/**
 * How a coin's number READS on the board — the bare figure.
 *
 * ── It IS a bet multiplier, and it is still printed without the x ──
 *
 * GDD §3.4 gives the coin as "Pays its value x bet" and names its twelve values
 * as `x1 / x2 / x5 … x5000`; I7 pays the board as `sum(coins) x bet`, and the
 * captures agree — a coin of `1` on a bet of 10 paid 10. So the number on the
 * cell is a multiplier and the counter that follows it is money.
 *
 * An `x` prefix was tried and taken out: it is not how the game looks.
 * Vladimir's call, 2026-08-21. The ambiguity is real and is accepted.
 *
 * Kept as a function rather than inlined, because whatever the label becomes,
 * the composite's cache key has to follow it — see goldenCellTexture. The one
 * property it must not lose is BET-INDEPENDENCE: a currency amount would have to
 * be re-printed on every bet change and the cache key would need the bet in it.
 *
 * The collector prints the same way. Its total is a sum of coin multipliers, so
 * it is the same kind of number.
 */
export const goldenValueLabel = (value) => `${value}`;

export const coinSkinForValue = (value) => {
  const v = Number(value) || 0;
  if (v >= 1000) return "coin1"; // bright gold
  if (v >= 100) return "coin2"; // dark gold
  if (v >= 10) return "coin3"; // silver
  return "coin4"; // bronze
};

/**
 * The +FS scatter's skin, from the spins it awards.
 *
 * `bs` carries `add_1`, `add_2`, `add_3` — the number is DRAWN ART, exactly as
 * the multiplier's x2/x3/x5/x10 are, so a `+2` is a skin and never a text. We
 * were asking for the default skin, which has no number on it at all, and that
 * is why the cell landed blank.
 *
 * `fs_count` is 1..3 per the BE doc. Anything else returns null and the cell
 * shows the plain frame rather than a wrong number.
 */
export const plusSkinForCount = (count) => {
  const n = Number(count);
  return n >= 1 && n <= 3 ? `add_${n}` : null;
};

/**
 * The flat texture for one outcome.
 *
 * Every one of these spines is already declared in symbolData, so the textures
 * come out of the same cache the reels use.
 */
/**
 * The SPINE one outcome is drawn from — its name and its skin.
 *
 * Split out of outcomeTexture so the flat picture and the live spine can never
 * name different artwork. The landing bounce needs exactly this pair, and
 * working it out twice is how a cell ends up bouncing as one symbol and
 * standing as another.
 *
 * @param   {{symbolId:number,value:number,fsCount:number|null}} item
 * @returns {{spineName:string,skin:string|null}|null}
 */
export const goldenCellSpine = (item) => {
  if (!item) return null;
  const { symbolId, value } = item;

  if (symbolId === COIN_SYMBOL_ID) {
    return { spineName: "c", skin: coinSkinForValue(value) };
  }
  if (symbolId === MULTIPLIER_SYMBOL_ID) {
    // x2 / x3 / x5 / x10 — "the only four it can be", per MULTIPLIER_VALUE_TO_SKIN.
    // Null for anything else, so an unknown value draws nothing rather than the
    // wrong multiplier.
    const skin = MULTIPLIER_VALUE_TO_SKIN[Number(value)];
    return skin ? { spineName: "mul", skin } : null;
  }
  if (symbolId === COLLECT_SYMBOL_ID) {
    return { spineName: "cl", skin: null };
  }
  if (symbolId === SCATTER_1_SYMBOL_ID) {
    return { spineName: "sc", skin: null };
  }
  if (symbolId === SCATTER_2_SYMBOL_ID) {
    return { spineName: "scg", skin: null };
  }
  if (symbolId === SCATTER_3_SYMBOL_ID) {
    // ── It ARRIVES as the golden scatter, closed ──
    //
    // The +FS never just lands: the cell stops on `scg`, its doors open, and a
    // strip of +1/+2/+3 spins inside that window — see plusScatterConfig. So
    // the picture the cell lands with is the SHUT door, and the `bs` art with
    // its number is what the window opens onto.
    //
    // `revealed` marks the cell whose sequence has already run, exactly as it
    // does for SC1 above.
    if (!item.revealed) return { spineName: "scg", skin: null };
    return { spineName: "bs", skin: plusSkinForCount(item.fsCount) };
  }
  return null;
};

const outcomeTexture = (item) => {
  if (!item) return null;
  const { symbolId, value } = item;

  if (symbolId === COIN_SYMBOL_ID) {
    return SpineTextureExtractor.getSkinTexture("c", coinSkinForValue(value));
  }
  if (symbolId === MULTIPLIER_SYMBOL_ID) {
    // x2 / x3 / x5 / x10 — "the only four it can be", per MULTIPLIER_VALUE_TO_SKIN.
    const skin = MULTIPLIER_VALUE_TO_SKIN[Number(value)];
    return skin ? SpineTextureExtractor.getSkinTexture("mul", skin) : null;
  }
  if (symbolId === COLLECT_SYMBOL_ID) {
    return SpineTextureExtractor.getSkinTexture("cl", null);
  }
  if (symbolId === SCATTER_1_SYMBOL_ID) {
    return SpineTextureExtractor.getSkinTexture("sc", null);
  }
  if (symbolId === SCATTER_2_SYMBOL_ID) {
    // ── The picture is WHAT LANDED, or the closed door ──
    //
    // The default pose is `static`: the gold window shut, which is how SC1
    // ARRIVES and the only honest picture before its sequence has run.
    if (!item.revealed) return SpineTextureExtractor.getSkinTexture("scg", null);

    // A PACKAGE was won. The settled picture is the open gold window with the
    // card inside it, pips lit and spin count printed — the same composite the
    // board's own SC1 keeps, built from the same table, so a scatter that came
    // out of the feature and one that landed on the reel are the same object.
    //
    // The cell carries the whole package already: the server writes
    // `[coord, "12", fs_count, fs_level]`, which is exactly the (value, tier)
    // pair GAMBLE_TIERS is keyed on. Nothing is inferred.
    //
    // Not warmed at boot, deliberately. warmRevealedCards already builds every
    // CARD, so what is left is one composite for one cell — and this file's own
    // rule is that the boot set covers a boardful of first landings, not a
    // single cell that arrives on its own.
    const tier = item.fsLevel || 0;
    if (tier > 0) {
      const landed = gambleStaticCell({ tier, value: item.fsCount || 0 });
      if (landed?.texture) return landed.texture;
      // The capture failed. The closed door is visibly wrong and therefore
      // gets fixed — the same choice toStaticCell makes for the same reason,
      // and far better than an empty window claiming the player won nothing.
      return SpineTextureExtractor.getSkinTexture("scg", null);
    }

    // A BLANK. `revealed_static` is the open, decided window — the same pose
    // RevealSymbolController holds live, so the flat picture the cell keeps is
    // the one it was already showing and the handover is invisible.
    return SpineTextureExtractor.getSkinTexture("scg", null, [
      "revealed_static",
    ]);
  }
  if (symbolId === SCATTER_3_SYMBOL_ID) {
    // The closed door until its reveal has run — see goldenCellSpine. A cell
    // painted before the doors open must not already show the answer.
    if (!item.revealed) {
      return SpineTextureExtractor.getSkinTexture("scg", null);
    }
    // Settled. The `+N` art, skinned by the spins it awards.
    //
    // NOT yet the composite of the open window with the number inside it — the
    // gamble scatter has one (see SCATTER_2 above) and this owes the same, so
    // the cell does not pop from an open window back to a plain symbol when the
    // feature repaints the board.
    return SpineTextureExtractor.getSkinTexture(
      "bs",
      plusSkinForCount(item.fsCount),
    );
  }
  return null;
};

/**
 * What a Golden cell can become — EVERY outcome, not this round's.
 *
 * ── The strip must not know the answer ──
 *
 * Built from the round's own outcomes, a two-cell feature spins a track made of
 * exactly the two things that are going to land on it. The player watches the
 * result go past four times before it stops. There is no anticipation left,
 * because there is nothing it could have been.
 *
 * So the track spins the whole pool and stops on what the server sent — which is
 * the same rule the reels themselves follow: the strip is a SHOW, the landing is
 * the outcome, and the two are related only at the moment of stopping.
 *
 * ── The pool, from the BE doc ──
 *
 * Coin values: 1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000 — all twelve,
 * so all four skins go past. Multipliers: x2, x3, x5, x10, "the only four it can
 * be". The collector. And the scatters that can actually drop HERE:
 *
 *   SC (11)   drops in all modes, including inside the coins game
 *   SC1 (12)  the main game only, and only as the 3rd scatter
 *   +FS (16)  a coins game inside free spins only — so NOT in this list
 *
 * The +FS scatter is deliberately absent: SymbolData's SPIN_FILLER_ALLOWED makes
 * the same argument about the reels, that a player who sees a symbol whip past
 * has been shown a near-miss on something that was never possible. When the free
 * spins side is built it gets its own pool with +FS in and SC1 out.
 *
 * Never a paying symbol: a Golden cell cannot become one.
 */
const COIN_VALUES = [
  1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000,
];

/** Everything both games share: the twelve coins, the four multipliers, the magnet. */
const GOLDEN_STRIP_COMMON = [
  ...COIN_VALUES.map((value) => ({ symbolId: COIN_SYMBOL_ID, value })),
  ...[2, 3, 5, 10].map((value) => ({ symbolId: MULTIPLIER_SYMBOL_ID, value })),
  { symbolId: COLLECT_SYMBOL_ID, value: 0 },
];

/**
 * The BASE game's pool.
 *
 * SC drops in every mode. SC1 — the gamble scatter — drops "only in the main
 * game (and in a coins game started from the main game)", always as the 3rd
 * scatter. So both belong here.
 */
// ── FOUR things spin, in both games: coin, multiplier, magnet, golden scatter ──
//
// Vladimir, 2026-09-18, after watching the reference game: *"No bonus, or bs
// symbols or scatters in golden mystery spinning strips. Only multiplier,
// coins, magnet, and golden scatter."*
//
// So the plain scatter (11) is out, and so are the three `+FS` variants (16)
// below. The golden scatter is the only scatter a cell ever spins past — and
// in free spins it is what the `+FS` arrives AS: the window lands closed, it
// opens, and a strip inside IT spins the +1/+2/+3.
//
// ── Taking an outcome out of the pool cannot break a landing ──
//
// The pool is only what spins PAST. The cell lands on the item the round
// carries, injected by the spinner — see play({ outcome, items }) — so a plain
// SC that does drop still paints as a plain SC. What changes is only what the
// player sees on the way to it.
export const GOLDEN_STRIP_ITEMS = [
  ...GOLDEN_STRIP_COMMON,
  { symbolId: SCATTER_2_SYMBOL_ID, value: 0 },
];

/**
 * The FREE SPINS pool — a different set, not a copy.
 *
 * SC1 is OUT: the BE doc scopes it to the main game and a coins game started
 * from the main game, so inside the room it cannot drop. +FS is IN: it drops
 * "only in a coins game started inside freespins", awarding 1-3 spins and
 * counting toward the level.
 *
 * SC stays. GDD invariant I14 says PLUS "replaces SC/SC1" in the bonus game, but
 * the BE doc is more precise and it is what the server does: SC "drops in all
 * modes and inside the coins game". It is also how the player fills the boxes,
 * so removing it would be wrong twice over.
 *
 * Kept as its own list rather than a filter, because the reason each symbol is
 * in or out is a rule, and a rule is worth writing down where it applies.
 */
// The same four. Kept as its own export because each game asks for its pool by
// name, and this is the one where the golden scatter goes on to open into a
// `+FS` — see GOLDEN_STRIP_ITEMS above.
export const GOLDEN_STRIP_ITEMS_FREE_SPINS = [
  ...GOLDEN_STRIP_COMMON,
  { symbolId: SCATTER_2_SYMBOL_ID, value: 0 },
];

/**
 * Build every Golden cell picture at boot.
 *
 * ── The stall this exists for ──
 *
 * A settled cell's picture is a render-to-texture, and it was built the first
 * time that outcome appeared — which is mid-feature, on the frame the track
 * stops. A wide board settles several cells at once and each one paid for its
 * own capture in the same breath: `[Violation] rAF took 1063ms` on a large
 * round. A whole second, at the exact moment the player is watching the cells
 * land.
 *
 * Same argument and the same shape as warmRevealedCards:
 * pay it at boot, where nothing is animating, into the same cache the runtime
 * lookup reads — so a miss still works, it just costs what it used to.
 *
 * ── What it can and cannot cover ──
 *
 * The two strip pools ARE the finite set of first landings: twelve coins, four
 * multipliers, the magnet and the scatters of both games. That is the case that
 * stalls, because a first landing is every cell at once.
 *
 * A chain's later values cannot be pre-built and are not meant to be — a coin
 * multiplied to 15 and a magnet holding 47 are numbers computed at runtime, and
 * they arrive one or two cells at a time rather than a boardful.
 *
 * Deduplicated across the two pools, because they overlap by seventeen entries
 * and the cache key is the same for both.
 */
export const warmGoldenCells = () => {
  const seen = new Set();
  const warm = (item) => {
    const key = `${item.symbolId}|${item.value ?? 0}|${item.fsCount ?? ""}|${
      item.revealed ? "r" : ""
    }`;
    if (seen.has(key)) return;
    seen.add(key);
    goldenCellTexture(item);
  };

  for (const item of GOLDEN_STRIP_ITEMS) warm(item);
  for (const item of GOLDEN_STRIP_ITEMS_FREE_SPINS) warm(item);
  // The emptied coin — what a cell stands as between a magnet taking it and its
  // respin. Not in either pool, because the strip never spins a blank one past.
  warm({ symbolId: COIN_SYMBOL_ID, value: 0 });
  // The gamble scatter's OPEN window. Not in either pool for the same reason:
  // the strip spins SC1 as a closed door, and this is the picture it settles on
  // after its own reveal has run. One more capture at boot, and it is the one
  // that would otherwise be built on the frame the doors finish opening.
  warm({ symbolId: SCATTER_2_SYMBOL_ID, value: 0, revealed: true });

  // ── Left the strip, can still LAND ──
  //
  // The plain scatter and the three `+FS` variants came out of both pools on
  // 2026-09-18 — the player must not see them spin past. The server can still
  // drop any of them on a cell, and a cell that lands on a picture nothing
  // warmed pays for its capture on the frame it stops, which is the stall this
  // whole function exists to prevent. Four textures, built at boot like the
  // rest.
  warm({ symbolId: SCATTER_1_SYMBOL_ID, value: 0 });
  // BOTH pictures for each count: the closed door it lands as, and the open
  // frame with the `+N` inside that it keeps once its window has run. The
  // second is the one the cell rides the next spin out on, so it is worth its
  // capture at boot exactly as the gamble scatter's open window is.
  for (const fsCount of [1, 2, 3]) {
    warm({ symbolId: SCATTER_3_SYMBOL_ID, value: 0, fsCount });
    warm({ symbolId: SCATTER_3_SYMBOL_ID, value: 0, fsCount, revealed: true });
  }
};

/**
 * The golden mystery layer, as a live spine inside the mystery's window.
 *
 * Built rather than borrowed from the overlay's pool: a slot object lives in the
 * fit container's unit space, and a pooled spine is already positioned and
 * scaled for the whole cell. Same factory shape, and the same sizing rule, as
 * the revealed symbol B1 injects — cellHeight / SYMBOL_HEIGHT.
 *
 * No animation is set here. `myg`'s setup pose IS the golden sign at rest, which
 * is what the doors should open onto; the controller plays `reveal` once the
 * doors are open.
 */
export const buildGoldenMysterySpine = ({ parent, cellHeight }) => {
  const info = symbolData[BONUS_SYMBOL_ID];
  if (!info?.spine || !parent) return null;

  let spine = null;
  try {
    spine = Spine.from(`${info.spine}-skeleton`, `${info.spine}-atlas`);
  } catch (e) {
    console.warn("[GoldenMystery] Could not build the golden layer:", e);
    return null;
  }
  if (!spine) return null;

  spine.scale.set(cellHeight / Settings.SYMBOL_HEIGHT);
  parent.addChild(spine);
  return spine;
};

/**
 * An empty strip cell. One reused sprite — a spinning strip must never allocate
 * per frame.
 */
// ── A/B SWITCH for the label texture cache ──
//
// true  numbers are SPRITES pointing at textures rasterised once (fast — this
//       is the optimisation that stopped ~115 canvas rasterisations a second)
// false numbers are live PIXI.Text, restyled per wrap, exactly as before it
//
// Here to settle one question and then be deleted: when the cells scramble on
// resize, is the cache responsible? Flip to false, resize, and compare. If the
// scrambling survives, it is older than this change and the search moves on.
const USE_LABEL_CACHE = true;

const createCell = () => {
  const cell = new PIXI.Container();
  cell.name = "golden_mystery_cell";

  const sprite = new PIXI.Sprite();
  sprite.anchor.set(0.5);
  sprite.name = "golden_mystery_art";
  // ── Snap to whole pixels while moving ──
  //
  // Borrowed from the base reel and Pot Collection, which both set this on
  // every spinning sprite and which is why their spins look cleaner than this
  // one did. A sprite travelling at a fractional speed otherwise lands on
  // sub-pixel positions and is bilinearly resampled EVERY frame, which reads
  // as shimmer on the art and softness on the digits.
  //
  // It belongs on the Sprite, not the Container — PIXI.Container has no such
  // property, so the container-level assignments in Pot Collection do nothing.
  sprite.roundPixels = true;
  cell.addChild(sprite);

  // The value, for the outcomes that have one. Created empty and reused — a
  // spinning strip must never allocate per frame — and left blank for a
  // collector or a scatter, which carry no number.
  // Empty and unstyled-for-size: renderCell sets the scaled style before the
  // cell is ever shown, and a size baked in here would be the one it rasterises
  // at if that ever stopped being true.
  // ── A second glyph, for the COLLECTOR's number only ──
  //
  // A digit's counter — the hole in a 6, 8, 9, 0, 4 — is a hole. The fill does
  // not cover it and a stroke is drawn CENTRED on the outline, so at the
  // collector's weight it closes that hole almost but not quite: what shows
  // through the gap is the artwork behind, which on the magnet is dark. That is
  // the black dot.
  //
  // So the collector's number is drawn twice. The one behind carries a fatter
  // stroke in the OUTLINE's own colour, which does close the counter; the one in
  // front is the real glyph, unchanged. The hole ends up the colour of the
  // outline instead of the colour of whatever is underneath, and the digit is not
  // thickened, because the fattening is hidden behind it.
  //
  // The COINS do not get this. Their numbers were right as they were, and a
  // second glyph on every strip cell is cost for nothing.
  //
  // ── SPRITES, not live Text ──
  //
  // Both numbers used to be `PIXI.Text` restyled on every wrap. `Text.set
  // style` has no equality check, so that forced a canvas re-rasterisation and
  // a texture upload per cell per wrap — about 115 a second on a full board,
  // which is why fifteen of these cost more than the entire 5x6 reel set.
  //
  // The pixels are unchanged: textLabelCache still uses PIXI.Text to draw
  // them, once, and these sprites point at the texture it made. Same
  // rasteriser, same style, same resolution.
  //
  // Added FIRST so it draws underneath. renderCell keeps its transform in step.
  const makeGlyph = (name) => {
    const glyph = USE_LABEL_CACHE
      ? new PIXI.Sprite()
      : new PIXI.Text("", FONT_STYLES.goldenCoin.coin1);
    glyph.anchor.set(0.5);
    glyph.name = name;
    glyph.roundPixels = true;
    return glyph;
  };

  const backing = makeGlyph("golden_mystery_value_backing");
  cell.addChild(backing);

  const text = makeGlyph("golden_mystery_value");
  cell.addChild(text);

  cell.__art = sprite;
  cell.__value = text;
  cell.__valueBacking = backing;
  return cell;
};

/** Blank a glyph, whichever kind it is. */
const clearGlyph = (glyph) => {
  if (USE_LABEL_CACHE) glyph.texture = PIXI.Texture.EMPTY;
  else glyph.text = "";
};

/**
 * Put `label` on `glyph`, and report the width the squeeze maths needs.
 *
 * Cached path: one rasterisation ever, reused as a texture.
 * Live path:   the original — restyle and let PIXI redraw it.
 */
const drawGlyph = (glyph, label, style, styleKey, fontSize) => {
  if (USE_LABEL_CACHE) {
    const entry = getLabelTexture(label, style, styleKey, fontSize);
    glyph.texture = entry?.texture ?? PIXI.Texture.EMPTY;
    return entry;
  }
  glyph.text = label;
  glyph.style = style;
  return { width: glyph.width };
};

/**
 * Draw one item into a cell.
 *
 * Scaled by the reels' own rule — cellHeight / SYMBOL_HEIGHT — because every
 * texture here was captured at the skeleton's native size, exactly like the
 * symbols on the reels.
 */
const renderCell = (cell, item, cellWidth, cellHeight) => {
  const sprite = cell?.__art;
  if (!sprite) return;

  const texture = outcomeTexture(item);
  sprite.texture = texture || PIXI.Texture.EMPTY;
  // EXACTLY the rule the reels use — cellHeight / SYMBOL_HEIGHT, with no
  // reference to the texture's own size. Fitting to the texture makes a
  // spinning cell smaller than the spine that replaces it, because a captured
  // frame includes effect art that extends past the symbol.
  const scale = cellHeight / Settings.SYMBOL_HEIGHT;
  sprite.scale.set(scale);

  const text = cell.__value;
  if (!text) return;

  // ── Who prints a number ──
  //
  // The coin, always. The collector, once it has collected — its value exists
  // nowhere in the payload and is computed by goldenMysteryScript, which is why
  // it arrives here as an ordinary item value.
  //
  // The multiplier never: it wears its value as its SKIN (x2/x3/x5/x10 are drawn
  // art, not text).
  //
  // A value of ZERO prints nothing, and that is a state we use: when the magnet
  // takes the coins, they are set to zero so they stand there empty until they
  // respin. The documents say the coins are REMOVED, but there is no clip for a
  // coin leaving — so emptying them is the honest half of it, and a fade can be
  // dropped in later without changing anything here.
  const prints =
    (item?.symbolId === COIN_SYMBOL_ID ||
      item?.symbolId === COLLECT_SYMBOL_ID) &&
    (item?.value || 0) > 0;
  if (!prints) {
    clearGlyph(text);
    if (cell.__valueBacking) clearGlyph(cell.__valueBacking);
    return;
  }

  // The collector has its own style — red on white with a blue glow. Used here
  // as well as on the live spine, so the number does not change appearance the
  // moment `collect_end` takes the spine away.
  const skin =
    item.symbolId === COLLECT_SYMBOL_ID
      ? "collector"
      : coinSkinForValue(item.value);
  const value = goldenValueLabel(item.value);

  // ── SCALED, not resized ──
  //
  // The size alone is not the style. `strokeThickness: 2` and
  // `dropShadowDistance: 3` were authored against fontSize 13, so a size swap
  // leaves them at their original PIXELS: at 48px the outline is a quarter as
  // thick as drawn and the shadow sits almost under the glyph, which is why the
  // shadow looked wrong. Everything measured in pixels has to move with the
  // size, or the design only holds at one resolution.
  //
  // scaleStyle is the house helper for exactly this — it scales
  // strokeThickness, dropShadowDistance and dropShadowBlur by the same ratio
  // and leaves the colours alone. Same call the Pot Collection dialogs make.
  //
  // A new object, never a mutation: FONT_STYLES is shared, and writing a size
  // into it would fix that size for every cell at every resolution.
  const style = FONT_STYLES.goldenCoin[skin] || FONT_STYLES.goldenCoin.coin1;
  const fontSize = cellHeight * GOLDEN_VALUE_FONT_SIZE;
  // Rasterised once per (skin, size, label) and reused for ever after. The
  // style is still built by scaleStyle from the same shared object — the only
  // thing that changed is WHEN it is turned into pixels.
  const label = drawGlyph(text, value, scaleStyle(style, fontSize), skin, fontSize);

  // The bone's offset, reproduced by hand because a flat cell has no bones. The
  // 1.3 the bone would have passed down is NOT applied to the text: the font
  // size above is already in screen pixels, so multiplying again would just make
  // it 30% bigger than the rule says.
  text.scale.set(1);
  text.position.set(0, -COIN_VALUE_OFFSET_Y * scale);

  // ── Fit it inside the coin's face ──
  //
  // Done with the container's SCALE, not by shrinking the font again, so the
  // glyphs stay one rasterisation and the stroke keeps its drawn proportion.
  //
  // An X-only squeeze does distort the outline and the shadow, so it is bounded:
  // past COIN_VALUE_MIN_SQUEEZE both axes come down together, which keeps the
  // artwork's proportions and only makes the number smaller. Four digits at a
  // quarter of the cell needs about 0.8, so the common case never reaches the
  // bound and the widest values stay honest.
  const maxWidth = cellWidth * (1 - COIN_VALUE_PADDING * 2);
  // From the cache, not from `text.width`. Reading the width off a live Text
  // is what forced the synchronous rasterisation — the measurement WAS the
  // cost. A sprite's width is already known, and the cached number is the same
  // one the Text reported.
  const width = label?.width ?? 0;
  if (width > maxWidth) {
    const factor = maxWidth / width;
    if (factor >= COIN_VALUE_MIN_SQUEEZE) {
      text.scale.x = factor;
    } else {
      text.scale.set(factor);
    }
  }

  // ── The glyph behind, closing the collector's counters ──
  //
  // Same string, same size, same place, same squeeze — it must sit EXACTLY under
  // the front one or it reads as a shadow. Only two things differ: the fill is
  // the outline's colour rather than the digit's, and the stroke is fattened
  // until it meets itself inside a 6, a 0, an 8. That fattening is invisible,
  // because the real glyph is drawn on top of it at its own weight.
  //
  // Copied from the front's finished transform rather than recomputed, so the
  // two cannot drift when the squeeze bites.
  const backing = cell.__valueBacking;
  if (backing) {
    // The collector's number ONLY — a coin's reads correctly on its own art.
    if (item.symbolId !== COLLECT_SYMBOL_ID) {
      clearGlyph(backing);
      return;
    }
    drawGlyph(
      backing,
      value,
      scaleStyle(
        {
          ...style,
          fill: style.stroke ?? style.fill,
          strokeThickness:
            (style.strokeThickness ?? 0) * COLLECTOR_VALUE_BACKING_STROKE,
        },
        fontSize,
      ),
      // Its own key — same size and string as the front glyph, different
      // colours and a fatter stroke. Sharing a key would hand one of them the
      // other's pixels.
      `${skin}:backing`,
      fontSize,
    );
    backing.scale.copyFrom(text.scale);
    backing.position.copyFrom(text.position);
  }
};

/**
 * A cell's number, as its own object — for a LIVE spine to wear.
 *
 * ── Why this exists at all ──
 *
 * A live spine carries no number. The flat cell's one only arrives when the
 * feature hands the cell back, so any beat that replaces the picture with a
 * skeleton — the landing bounce, the collector's activation — would show the
 * symbol with its value missing. The value is not decoration on a coin: it IS
 * what the coin pays.
 *
 * ── Why it is NOT injected into the rig's text slot ──
 *
 * `c` carries `txt_slot_coin1..4` and `cl` carries `txt_slot_collector_value`,
 * and using them is the wrong answer here. A slot object is placed by the bone
 * every frame and that bone carries the rig's own scale, so the number would be
 * a different size from the flat one that replaces it a moment later and would
 * visibly jump at the handover. (The reels do inject, in showCoinValueText, and
 * pay for it by dividing the font size back out by `spineScale * bone.a` — that
 * arithmetic exists precisely because the slot fights it.)
 *
 * Drawn in the cell's own container instead, in plain cell pixels, from the same
 * style, offset and squeeze as `renderCell`. So the flat picture and the live
 * one are the same number in the same place, and the swap is invisible in both
 * directions.
 *
 * @param   {Object} p
 * @param   {PIXI.Container} p.parent
 * @param   {{symbolId:number,value:number}} p.item What the cell is showing.
 * @param   {number} p.cellWidth
 * @param   {number} p.cellHeight
 * @param   {boolean} [p.atBone] True when a BONE will place it — see
 *   wearCellNumber. The hand-reproduced offset is then dropped, because the rig
 *   is doing the placing and it is the authority on where the number sits.
 * @returns {PIXI.Container|null} null when this cell prints nothing.
 */
export const buildCellNumber = ({
  parent,
  item,
  cellWidth,
  cellHeight,
  atBone = false,
}) => {
  // The same rule renderCell applies, and for the same reasons: the coin always
  // prints, the collector prints once it has collected, the multiplier never
  // does — it wears its value as a SKIN — and a value of zero prints nothing,
  // which is the state an emptied coin sits in.
  const symbolId = item?.symbolId;
  const value = item?.value || 0;
  const prints =
    (symbolId === COIN_SYMBOL_ID || symbolId === COLLECT_SYMBOL_ID) &&
    value > 0;
  if (!parent || !prints) return null;

  const holder = new PIXI.Container();
  holder.name = "golden_cell_value";

  // The collector has its own style — red on white with a blue glow. Every coin
  // tier has its own too, keyed by the same skin the artwork uses, so the number
  // matches the coin it is standing on.
  const skin =
    symbolId === COLLECT_SYMBOL_ID ? "collector" : coinSkinForValue(value);
  const style = FONT_STYLES.goldenCoin[skin] || FONT_STYLES.goldenCoin.coin1;
  const fontSize = cellHeight * GOLDEN_VALUE_FONT_SIZE;
  const label = goldenValueLabel(value);

  // ── The glyph behind, closing the collector's counters ──
  //
  // The COLLECTOR'S ONLY. A coin's number reads correctly on its own art, and
  // the backing was added for the hole in a 6 on the collect coin. Added first
  // so it draws underneath, exactly as renderCell orders them.
  if (symbolId === COLLECT_SYMBOL_ID) {
    const backing = new PIXI.Text(
      label,
      scaleStyle(
        {
          ...style,
          fill: style.stroke ?? style.fill,
          strokeThickness:
            (style.strokeThickness ?? 0) * COLLECTOR_VALUE_BACKING_STROKE,
        },
        fontSize,
      ),
    );
    backing.anchor.set(0.5);
    holder.addChild(backing);
  }

  const text = new PIXI.Text(label, scaleStyle(style, fontSize));
  text.anchor.set(0.5);
  holder.addChild(text);

  if (!atBone) {
    const scale = cellHeight / Settings.SYMBOL_HEIGHT;
    holder.position.set(0, -COIN_VALUE_OFFSET_Y * scale);
  }

  // Same squeeze rule as the flat cell, applied to the pair at once so they
  // cannot drift.
  const maxWidth = cellWidth * (1 - COIN_VALUE_PADDING * 2);
  if (text.width > maxWidth) {
    const factor = maxWidth / text.width;
    if (factor >= COIN_VALUE_MIN_SQUEEZE) holder.scale.x = factor;
    else holder.scale.set(factor);
  }

  parent.addChild(holder);
  return holder;
};

/**
 * The collector's total, drawn the moment the bolts land.
 *
 * A named entry point for one case of buildCellNumber, kept because the caller
 * has a total and not an item — see showCollectedTotal.
 */
export const buildCollectorNumber = ({
  parent,
  value,
  cellWidth,
  cellHeight,
}) =>
  buildCellNumber({
    parent,
    item: { symbolId: COLLECT_SYMBOL_ID, value },
    cellWidth,
    cellHeight,
  });

/**
 * The sound a landed cell makes.
 *
 * The reels play one for every symbol that lands (see useLandingAnimations) and
 * the feature played none at all — ten cells arriving in silence. A Golden cell
 * is a reel, so it gets the same treatment.
 *
 * All three are real files now — there are no stubs left in this table.
 * They are named anyway, because the wiring is the part that is easy to forget
 * once the assets arrive — dropping a file in then is all it takes.
 *
 * @param   {{symbolId:number}} item
 * @returns {string|null}
 */
export const goldenLandingSound = (item) => {
  switch (item?.symbolId) {
    case COIN_SYMBOL_ID:
      return SOUND_NAME.coin_land;
    case COLLECT_SYMBOL_ID:
      return SOUND_NAME.collect_land;
    case MULTIPLIER_SYMBOL_ID:
      return SOUND_NAME.multiplier_appear;
    // All three scatters announce themselves the same way. The gold one's own
    // moment is its DOOR, which is a separate sound fired where the door opens
    // — not here, where it has only arrived.
    case SCATTER_1_SYMBOL_ID:
    case SCATTER_2_SYMBOL_ID:
    case SCATTER_3_SYMBOL_ID:
      return SOUND_NAME.scatter_land;
    default:
      return null;
  }
};

/**
 * Which chime a paid round gets: the feature's, or the ordinary one.
 *
 * ── Why this is decided from the WINNING DATA ──
 *
 * `isFeaturePayout` is set in exactly one place — the Golden Mystery's payout
 * entry in RGS — and it means "pay it, do not decorate it". So its presence in
 * the data the win controller is already holding is the whole test. No round
 * flag to stamp, and nothing for a second code path to forget.
 *
 * ── Why it is not simply always played ──
 *
 * The feature pays often enough to produce big wins, and a big win already has
 * a banner and its own music. So this is placed at the basic_win sites and
 * inherits their `!isAnimatedWin` gate: a round that earned a celebration
 * plays the celebration, and this is what the quiet rounds get instead.
 *
 * The same swap scatter_win makes on a trigger, for the same reason.
 *
 * @param   {object} winningData keyed by index into round.wins
 * @returns {string} a SOUND_NAME value
 */
export const goldenWinSound = (winningData) => {
  const entries = Object.values(winningData || {});

  for (const entry of entries) {
    if (entry?.isFeaturePayout) return SOUND_NAME.golden_mystery_win;
  }

  // ── A round whose only win is the SCATTER LINE gets no chime at all ──
  //
  // Three scatters land and the round's `wins` carries one entry: the synthetic
  // scatter win, worth nothing. There is no ordinary symbol celebrating, so
  // there is nothing for `basic_win` to be the sound OF — and the trigger has
  // its own sound a beat later, `scatter_win`, which the win controller says
  // outright "REPLACES basic_win here".
  //
  // It played anyway, and was then cut mid-note by the doors opening
  // (`cutSound` in gambleScatterConfig) — which is what the player hears: a
  // chime that starts and is snatched away before the real one arrives.
  // Vladimir, 2026-09-22: "when we land on 3 scatters i can hear the win sound
  // briefly which makes no sense because we have special win sound for
  // scatters."
  //
  // EVERY entry, not any: a round that pays lines AND triggers still gets the
  // chime for the lines, plays it in full, and the doors cut it exactly as they
  // were designed to. Only the trigger-on-its-own case goes quiet.
  //
  // Null rather than a name — all four call sites already guard with
  // `if (winChime)`.
  // ── OFF (Vladimir, 2026-10-22) ── Cleopatra has no scatter_win to follow,
  // so 3 scatters played nothing: "make 3 scatters also play basic win for
  // now", as the 6 coins do.
  // if (entries.length && entries.every((entry) => entry?.isScatter)) return null;

  // ── The TRIGGER wins have their own (2026-10-28) ── the Hold & Win coin
  // line plays win_coin, a scatter line (3 scatters; the free spins' 2/3
  // scatter retriggers) win_scatter — louder than basic_win, they matter more.
  // A round with both: the Hold & Win's.
  if (entries.some((entry) => entry?.isCoin)) return SOUND_NAME.win_coin;
  if (entries.some((entry) => entry?.isScatter)) return SOUND_NAME.win_scatter;

  return SOUND_NAME.basic_win;
};

/**
 * Which slot on the rig holds this cell's number.
 *
 * `c` carries one per skin — txt_slot_coin1..4 — and `cl` carries
 * txt_slot_collector_value. All of them hang off the same bone, `value`.
 *
 * @param   {{symbolId:number,value:number}} item
 * @returns {string|null}
 */
const valueSlotFor = (item) => {
  if (item?.symbolId === COLLECT_SYMBOL_ID) return "txt_slot_collector_value";
  if (item?.symbolId === COIN_SYMBOL_ID) {
    const skin = coinSkinForValue(item.value);
    return skin ? `txt_slot_${skin}` : null;
  }
  return null;
};

/**
 * Put a cell's number ON a live spine, so it moves with the clip.
 *
 * ── Why the slot, and not the cell container ──
 *
 * buildCellNumber alone draws the number in the cell, which is right when
 * nothing is animating it — the collector's total, arriving over a clip that
 * does not touch the value bone. It is WRONG for a landing: `c.bounce` keys
 * both `coins` and `value`, so the coin squashes and springs while a number
 * drawn beside it sits perfectly still. The number is part of the coin, and it
 * has to move like it.
 *
 * addSlotObject is what makes it part of the coin. The slot places the object
 * from its bone every frame, and it also carries the slot's alpha, visibility,
 * draw order and clipping — all of it, already done, which is why none of that
 * is hand-rolled here.
 *
 * ── The counter-scale, and why it is not optional ──
 *
 * The bone's transform is applied every frame, and it carries the rig's own
 * scale — the 1.3 on `scale`, times whatever PIXI scale the spine was built at.
 * Left alone the number is that much the wrong size. So an INNER container is
 * set to 1/(spineScale x boneScale), read from the skeleton at REST: the two
 * cancel, the resting size is exactly the flat cell's, and the bone's ANIMATED
 * scale still rides on top. That last part is the bounce.
 *
 * Inner, and not the slot object itself, because the slot object's transform is
 * overwritten from the bone every frame — see the note in the body.
 *
 * Same arithmetic the reels do in showCoinValueText, which applies it to the
 * FONT SIZE for the very same reason: a slot object cannot carry a scale.
 *
 * @param   {Object} p
 * @param   {Object} p.spine The live spine to wear it.
 * @param   {{symbolId:number,value:number}} p.item
 * @param   {number} p.cellWidth
 * @param   {number} p.cellHeight
 * @returns {PIXI.Container|null} The injected object, for the caller to destroy.
 */
export const wearCellNumber = ({ spine, item, cellWidth, cellHeight }) => {
  const slotName = valueSlotFor(item);
  if (!spine || !slotName) return null;
  // A rig without the slot is not an error worth stopping for — the number
  // simply cannot be worn, and the cell still lands.
  if (!spine.skeleton?.findSlot?.(slotName)) return null;

  const worn = new PIXI.Container();
  worn.name = "golden_cell_value_slot";

  // ── The counter-scale needs a container the SLOT does not own ──
  //
  // `worn` is the slot object, and updateSlotObject does this to it every frame
  // (spine-pixi's Spine.js):
  //
  //   matrix.a = bone.a; … matrix.ty = bone.worldY;
  //   slotObject.transform.setFromMatrix(matrix);
  //
  // The WHOLE transform, replaced from the bone. So a scale set on `worn`
  // survives exactly until the next frame and then it is gone — which is what
  // happened: the correction was written, wiped, and the number rendered at
  // `spineScale x bone.a` of its authored size.
  //
  // It hid on desktop and only on desktop. The spine is scaled by
  // cellHeight / Settings.SYMBOL_HEIGHT, so at a 159px cell the factor is
  // 0.82 x 1.3 = 1.07 — a 7% error nobody sees. At a 53px cell it is 0.36, so
  // the number came out at a THIRD of its size, and the bounce swung that wrong
  // base around.
  //
  // So the scale goes on an inner container. Its transform is not the slot's, so
  // nothing overwrites it, and the squeeze inside buildCellNumber sits below it
  // and composes rather than fighting.
  const scaled = new PIXI.Container();
  scaled.name = "golden_cell_value_scale";
  worn.addChild(scaled);

  const number = buildCellNumber({
    parent: scaled,
    item,
    cellWidth,
    cellHeight,
    // The bone places it. Reproducing the offset as well would move it twice.
    atBone: true,
  });
  if (!number) {
    worn.destroy({ children: true });
    return null;
  }

  // Bone values are zero until the world transform has been computed once.
  try {
    spine.update(0);
  } catch {
    // A skeleton that cannot update will not place the object either; the
    // scale below then falls back to 1 and the number is merely the wrong size,
    // which is better than no number.
  }
  const bone = spine.skeleton?.findBone?.("value");
  const boneScale = Math.abs(bone?.a) || 1;
  const spineScale = spine.scale?.x || 1;
  const total = boneScale * spineScale;
  // On `scaled`, never on `worn` — see the note above.
  scaled.scale.set(total ? 1 / total : 1);

  spine.addSlotObject(slotName, worn);
  return worn;
};

/**
 * The settled cell, as ONE flat reel texture.
 *
 * Built from the SAME two functions the live track uses — createCell and the
 * renderCell the strip is running — so the picture that lands and the picture
 * that stays are the same pixels. Reproducing it separately is how the two drift
 * apart, and the drift always shows at the handover.
 *
 * Captured at NATIVE size, like every other symbol texture, so the reel scales
 * it by its own rule.
 *
 * @param   {{symbolId:number,value:number}} item What the track landed on.
 * @returns {PIXI.Texture|null}
 */
export const goldenCellTexture = (item) => {
  if (!item) return null;

  // The key must follow what is DRAWN. Both the coin and the collector print a
  // number — the collector's being a total this client computed, not one the
  // server sent — so the label covers both. It stays correct if a coin's value
  // ever becomes a currency string, because the key would change with it.
  const prints =
    item.symbolId === COIN_SYMBOL_ID || item.symbolId === COLLECT_SYMBOL_ID;
  // The +FS scatter draws no text, but its NUMBER is in the artwork and its
  // `value` is 0 for every count — so without the count in the key all three
  // variants share one cached texture and whichever landed first wins for the
  // session. The key follows what is drawn, whether text or skin.
  const label = prints
    ? goldenValueLabel(item.value ?? "")
    : item.symbolId === SCATTER_3_SYMBOL_ID
      ? // ── TWO pictures per count, and `revealed` is what tells them apart ──
        //
        // A `+FS` cell is the closed golden door until its window has opened
        // and the plain `+N` after it — see outcomeTexture. Without the flag in
        // the key both share one entry, and since warmGoldenCells builds the
        // CLOSED one at boot, that is the one every settled cell got: the board
        // repainted a shut door over a window the player had just watched open.
        `${item.revealed ? "revealed:" : ""}+${item.fsCount ?? 0}`
      : // The gamble scatter draws no text either, but it has a picture PER
        // OUTCOME — the closed door it arrives as, the empty window a blank
        // settles on, and one card per package won. The composite cache is
        // keyed on symbol, value and label, and every one of them is symbol 12
        // at value 0, so without the package in the key they share one entry
        // and whichever was built first wins for the session.
        item.symbolId === SCATTER_2_SYMBOL_ID && item.revealed
        ? `revealed:${item.fsLevel ?? 0}:${item.fsCount ?? 0}`
        : "";

  return SpineTextureExtractor.getGoldenCellTexture(item, label, (args) => {
    const cell = createCell();
    renderCell(
      cell,
      item,
      args.cellWidth ?? Settings.SYMBOL_WIDTH,
      args.cellHeight ?? Settings.SYMBOL_HEIGHT,
    );
    args.parent.addChild(cell);
    return {
      container: cell,
      destroy: () => cell.destroy({ children: true }),
    };
  });
};

/**
 * The controller config for one Golden cell.
 *
 * Timings start from the gamble scatter's, which are tuned and proven, and are
 * the first thing to move once this is on screen.
 */
/**
 * The live spine for one feature symbol, for the moments a flat cell cannot act.
 *
 * The track lands on flat textures, which is right while it is moving and right
 * for the cell at rest. But a multiplier has to FIRE and a collector has to
 * COLLECT, and a picture cannot. So those moments build a real skeleton over the
 * cell, play it, and take it away again.
 *
 * @param {Object} p
 * @param {PIXI.Container} p.parent
 * @param {string} p.spineName  "mul", "cl", "upgrade"
 * @param {string|null} [p.skin]
 * @param {number} p.cellHeight
 */
export const buildFeatureSpine = ({ parent, spineName, skin, cellHeight }) => {
  if (!parent || !spineName) return null;

  // ── The atlas has to be on the GPU before the first frame ──
  //
  // PIXI uploads a texture lazily, the first time it is rendered — and that
  // upload is a synchronous gl.texImage2D that can miss the frame budget, which
  // shows as a stall or a blank frame. Every other controller in this codebase
  // calls this before building a spine; the overlay does it at boot for all
  // SYMBOL spines, which covers `mul` and `cl` but not `upgrade`, because that
  // one is an effect and not a symbol.
  //
  // Idempotent — an already-uploaded texture is a no-op in WebGL.
  SpineAtlasUploader.upload(spineName);

  let spine = null;
  try {
    spine = Spine.from(`${spineName}-skeleton`, `${spineName}-atlas`);
  } catch (e) {
    console.warn(`[GoldenMystery] no spine "${spineName}":`, e);
    return null;
  }
  if (!spine) return null;

  if (skin && spine.skeleton?.data?.findSkin) {
    const found = spine.skeleton.data.findSkin(skin);
    if (found) {
      spine.skeleton.setSkin(found);
      spine.skeleton.setSlotsToSetupPose();
    }
  }
  spine.scale.set(cellHeight / Settings.SYMBOL_HEIGHT);
  // ── Born INVISIBLE ──
  //
  // Spine.from hands back a skeleton in its setup pose and adds it to the stage
  // ready to draw. Whatever clip is queued next does not reach the skeleton
  // until the following tick, so a spine shown on creation paints one frame of
  // raw setup art first — a collector with no animation on it, a multiplier at
  // rest. That is the flash.
  //
  // It is raised to 1 by _playOnce, after the clip's first frame has been
  // applied. Same discipline as showWinAnimation: "make visible AFTER the
  // animation is queued".
  spine.alpha = 0;
  parent.addChild(spine);
  return spine;
};

/** The multiplier's skin for a value, or null when it is not one of the four. */
export const multiplierSkinFor = (value) =>
  MULTIPLIER_VALUE_TO_SKIN[Number(value)] || null;

/**
 * @param {Object}  [p]
 * @param {boolean} [p.freeSpins] Which game the cell is running in. It changes
 *   ONE thing — the pool the track spins — and that one thing matters: a player
 *   who sees a gamble scatter whip past inside free spins has been shown a
 *   near-miss on something that cannot happen. Same argument as
 *   SPIN_FILLER_ALLOWED makes about the reels.
 */
/**
 * Rasterise every number this strip can show, BEFORE it starts moving.
 *
 * The point of the label cache: a strip spins a KNOWN pool, so the one
 * unavoidable rasterisation per label is paid here — while the board is being
 * built, with no frame depending on it — instead of landing on whichever frame
 * a cell happens to wrap onto a value it has not drawn yet.
 *
 * Cheap and idempotent: the cache returns a hit for anything already made, so
 * calling it once per cell on the board costs one pass over a short list.
 *
 * @param {Array}  items      The strip's own pool.
 * @param {number} cellHeight Drives the font size, and so the cache key.
 */
export const warmGoldenLabels = (items, cellHeight) => {
  if (!Array.isArray(items) || !cellHeight) return;
  const fontSize = cellHeight * GOLDEN_VALUE_FONT_SIZE;

  const entries = [];
  for (const item of items) {
    const label = goldenValueLabel(item?.value);
    if (!label) continue;
    const skin =
      item.symbolId === COLLECT_SYMBOL_ID
        ? "collector"
        : coinSkinForValue(item.value);
    const style = FONT_STYLES.goldenCoin[skin] || FONT_STYLES.goldenCoin.coin1;
    entries.push({
      label,
      style: scaleStyle(style, fontSize),
      styleKey: skin,
      fontSize,
    });

    // The collector's second glyph — same string, its own colours. Only the
    // collector has one, so only the collector's is warmed.
    if (item.symbolId === COLLECT_SYMBOL_ID) {
      entries.push({
        label,
        style: scaleStyle(
          {
            ...style,
            fill: style.stroke ?? style.fill,
            strokeThickness:
              (style.strokeThickness ?? 0) * COLLECTOR_VALUE_BACKING_STROKE,
          },
          fontSize,
        ),
        styleKey: `${skin}:backing`,
        fontSize,
      });
    }
  }
  warmLabelTextures(entries);
};

export const buildGoldenMysteryConfig = ({ freeSpins = false } = {}) => ({
  // ── The LID on top: the ordinary mystery the player landed ──
  //
  // A golden cell is two layers now, and this is the upper one. `my` — the
  // same doors every other mystery opens with — plays its build-up, parts, and
  // fades away. What it uncovers is the frame below with the golden sign
  // already inside it.
  //
  // Before this, the FRAME did both jobs: it played the build-up and opened its
  // own doors, so a golden cell opened with the OLD rig's doors while every
  // other mystery on the board used the new ones. Two different reveals for the
  // same gesture.
  //
  // The timings mirror symbolData[9].reveal deliberately — it is the same clip
  // on the same rig, and the two must not drift. See the note there for why
  // `fadeOutEndMs` is stated as the moment the lid is GONE.
  //
  // This layer is PERMANENT. When `myg` grows its own frame the layer below
  // goes, and this one stays exactly as it is.
  lid: {
    anticipation: ["anticipation"],
    open: ["reveal"],
    // The frame below is uncovered on THIS event, exactly as an ordinary
    // reveal swaps its reel texture on it. Same rig, same key, same moment —
    // see symbolData[9].reveal.openingEvent.
    openingEvent: "show_symbol",
    fadeOutMs: 250,
    fadeOutEndMs: 500,
  },

  // The mystery frame, and the window the lid goes into.
  frame: {
    // The same slot the B1 reveal injects its revealed symbol into. What the
    // doors open onto is what belongs in there.
    slotNames: ["attachment_slot_symbol"],
    // ── NO LONGER PLAYED. Kept only as a fallback ──
    //
    // The build-up and the doors moved to `lid` above. These two stay for a
    // cell that somehow has no lid spine to run — a pool that could not build
    // one — so the feature degrades to the old single-layer behaviour instead
    // of opening onto nothing. See play() in GoldenMysteryCellController.
    // ── NO anticipation, and none is needed ──
    //
    // The LID plays the whole build-up above this. `myg` has no such clip and
    // is not missing one — an empty list makes the resolver find nothing rather
    // than reach for a wrong one.
    anticipation: [],
    // ── `reveal`: the gold sign burns away and the window opens ──
    //
    // One clip, HELD on its last frame for the rest of the feature — own art
    // gone, `attachment_slot_symbol` at full alpha. This was `reveal_spin` on
    // the old `my`, whose job was to open doors without fading its own frame.
    // `myg` has no doors: the lid above owns them.
    open: ["reveal"],
    // ── `spin`: the light chase, LOOPED while the track turns ──
    //
    // Vladimir: "spin is used to play while track is spinning inside it."
    // It also holds `golden_myst`, `golden_myst_bg` and `golden_myst_shine`
    // cleared, so the gold sign cannot come back over the strip.
    spinning: ["spin"],
    // ── And it KEEPS running once the track has landed ──
    //
    // The same clip, looped on, exactly as `anticipation_spin` was before it.
    // The old rig settled to `revealed_static` instead and every cell went dark
    // the instant it landed — the board finished the feature looking finished.
    //
    // `static` must NEVER be used here. It keys nothing, so the skeleton falls
    // back to setup and the gold sign reappears on top of the outcome.
    settle: ["spin"],
    // ── The cell celebrates like any other winning symbol ──
    //
    // `myg.win` is the gamble scatter's bounce, key for key — added by hand
    // because the rig shipped without one, exactly as it was added to `my`
    // before it. It keys `golden_myst_main`, the bone the frame, the lid and
    // the outcome all hang off, so the whole cell moves as one thing, and it
    // holds the gold sign hidden while it does.
    //
    // Played on its own track so the light chase above keeps running under it.
    // See GoldenMysteryCellController.playWin.
    win: ["win"],
  },

  // The golden layer above it.
  golden: {
    // Once. Ends with its own art gone and the slot at full alpha, and is HELD
    // on that last frame for the rest of the feature — see the controller.
    reveal: ["reveal"],
    slotNames: ["attachment_slot_symbol"],
  },

  // The collector's total, drawn over the live spine the moment the bolts land —
  // see buildCollectorNumber and GoldenMysteryCellController.showCollectedTotal.
  // On the config because it is a decision about ART, which the controller has
  // none of.
  buildCollectorNumber,

  // ── Which spine a landed cell bounces on ──
  //
  // On the config for the same reason buildCollectorNumber is: the shared
  // controller knows how to play a landing clip but has no business knowing
  // that a x3 is `mul/mul_3`. It asks, and a null answer means this outcome has
  // no spine to bounce — which is a skipped animation, never a stalled feature.
  landingSpine: goldenCellSpine,

  // The number a live spine wears while it plays — see buildCellNumber. On the
  // config for the same reason the two above are: it is a decision about type
  // and style, which the shared controller has none of.
  buildCellNumber,

  // The same number, worn BY a live spine so it moves with the clip — see
  // wearCellNumber. Used by the landing bounce, which animates the value bone.
  wearCellNumber,

  // What a landed cell sounds like — see goldenLandingSound.
  landingSound: goldenLandingSound,

  strip: {
    items: freeSpins ? GOLDEN_STRIP_ITEMS_FREE_SPINS : GOLDEN_STRIP_ITEMS,
    createCell,
    renderCell,
    // Called by the cell controller before the strip is built — see
    // warmGoldenLabels.
    warmLabels: warmGoldenLabels,
    // ── Cruise: milliseconds to travel one cell ──
    //
    // The one knob left, now that the landing is derived rather than tuned.
    // Everything downstream follows it: the deceleration starts from this
    // velocity, so the stop shortens with it — 170 gave a 291ms landing, 130
    // gives 230ms — and roughly 20 cells whip past in the 2.6s spin instead of
    // 15, which is the part that reads as energy.
    //
    // The Hermite landing stays monotonic all the way down to 90, checked at
    // every distance the strip can produce, so this can move further without
    // the curve folding back on itself.
    speedMs: 130,
    accelMs: 320,
    // ── FLAT. The cell settles ONCE, and the bounce is that settle ──
    //
    // BackOut with an overshoot springs the cell 5.7% of the stop distance past
    // the line and pulls it back. `c.bounce` then does its own spring — down
    // 0.133 of a cell in 133ms, back up past rest, settled at 500ms — starting
    // from the standstill the first spring had already reached. Two settles back
    // to back, and the second one reads as a jerk because the eye has already
    // been told the cell arrived.
    //
    // Zero removes the strip's spring and leaves the deceleration. The landing
    // clip is then the only thing that bounces, which is what it was drawn to
    // be — and it is how the reels work: the reel's stop and the symbol's
    // landing clip are one motion, not two.
    //
    // Unused now that the landing hands over — landingExitSpeed replaces the
    // curve entirely. Kept at 0 so a rollback lands flat rather than springing.
    overshoot: 0,

    // ── The landing ends at the BOUNCE's speed, so the two connect ──
    //
    // From `c.json`, the `coins` bone in `bounce`:
    //
    //   t 0.0000   y  0        at rest
    //   t 0.1333   y -25.76    down 25.76 skeleton units
    //   t 0.3333   y  +3.03    back up past rest
    //   t 0.5000   y  0        settled
    //
    // ── The number that matters is the TANGENT, not the average ──
    //
    // 25.76 units over 133.3ms averages 0.1328 of a cell in that time, and
    // matching THAT is wrong: the segment is a Bézier that eases in, so the
    // clip's first frames are far slower than its middle. Its own handles say
    // so — the y curve runs P0 (0, 0) -> P1 (0.044, -4.04), and the tangent at
    // t=0 is the slope between them:
    //
    //   4.04 / 0.044 = 91.8 units per second
    //
    // which is 2.1x slower than the average. Matched to the average the cell
    // arrived twice as fast as the clip begins, and the eye read the difference
    // as the cell stopping and waiting before the drop took hold.
    //
    // The clip peaks at 0.0229 cell/frame around 60ms in and is back to zero at
    // 133ms; the strip now joins it at its 0.0079 opening and the acceleration
    // that follows belongs to the clip.
    //
    // The coin's, and not each symbol's own: `mul`, `sc` and `bs` run 667ms
    // clips with their own openings, but the coin is what fills a Golden board
    // and one strip cannot land at four different speeds.
    //
    // Written as the two handle numbers rather than the answer (0.000473) so a
    // re-export can be checked against it by reading the JSON again.
    landingExitSpeed: 4.04 / 0.044 / Settings.SYMBOL_HEIGHT / 1000,

    // stopMs is GONE on purpose. With both ends of the deceleration fixed the
    // duration is arithmetic — 2 x distance / (v0 + v1) — and the spinner
    // derives it per landing. The old fixed 560ms was roughly double what this
    // strip's own distance and speeds allow, which is why the cell drifted the
    // last third of the way in and then stopped dead.
    windUpDistance: 0.22,
    windUpMs: 300,
  },

  // How long the track runs before it is told where to stop. The golden reveal
  // is 1.0s of that, and the track is hidden behind it until the boom, so the
  // player sees roughly the remainder.
  spinDurationMs: 2600,

  // ── The cascade ──
  //
  // Every track starts together and they stop one after another, in board order
  // — reel 0's rows, then reel 1's, and so on. This is what each cell waits ON
  // TOP of spinDurationMs, multiplied by its place in that order.
  //
  // ── A RESPIN is shorter, on purpose, and by exactly one lid ──
  //
  // The first landing spins for 2.6s, but the first SECOND of that is hidden:
  // `myg.reveal` is a 1.0s lid and the track is behind it until the boom. So the
  // player only ever sees about 1.6s of it.
  //
  // A respun cell has no lid — it is visible from the first frame — so spinning
  // it for 2.6s again would make it feel much longer than the landing it is
  // meant to echo. 1600 is the same VISIBLE time.
  //
  // The stagger below is deliberately NOT halved with it. The gap between stops
  // is the rhythm of the feature, and it should not change just because the
  // cells are spinning for less time.
  respinDurationMs: 1600,

  // The arithmetic to dial it by: LAST STOP = spinDurationMs + (cells - 1) x
  // this. Ten cells at 750ms is 6.75s between the first stop and the last, on
  // top of the 2.6s spin — so a full board finishes at about 9.4s.
  //
  // SHARED with the respins, which is the point: one rhythm for the feature, not
  // one per phase.
  //
  // Dialled up three times from 180, which read as simultaneous, through 360,
  // which read as merely staggered. This is the biggest feature moment in the
  // game and each cell is a separate prize, so each one gets its own beat —
  // three quarters of a second, long enough that the eye lands on the value
  // before the next cell moves. SIGNED OFF at this value.
  //
  // The cost is honest: on a ten-cell board this feature owns the screen for
  // nine and a half seconds. That is the intended drama, not an oversight — and
  // it is why the number lives here, alone, with the arithmetic beside it.
  //
  // ── A WHOLE NUMBER OF CELLS: 780 = 6 x speedMs ──
  //
  // 750 was the signed-off beat and 780 is within a frame and a half of it, so
  // the rhythm is unchanged. What changes is that every cell now lands the SAME
  // WAY.
  //
  // A stop shortens its journey by the drift — how far past the grid line the
  // strip happens to be when it is told to stop — so the curve finishes exactly
  // on the line instead of being corrected onto it. The landing's duration is
  // then 2 x distance / (v0 + v1), so a different drift means a different
  // duration: at 750, 750/130 = 5.769 cells, and that 0.769 accumulated
  // differently down the wave. The cells landed over anything from 184ms to
  // 429ms — a 2.3x spread, which is why they read as slightly different speeds
  // even though the exit velocity is one fixed number for all of them.
  //
  // 780/130 = exactly 6, so every cell is at the same sub-cell offset when it is
  // told to stop. Same drift, same distance, same 245ms landing, all eight or
  // ten of them — offset in time, identical in motion.
  //
  // TIED TO speedMs. Change the cruise and this has to be a multiple of it again
  // or the spread comes back.
  stopStaggerMs: 780,
});

/**
 * The Golden Mystery's MOTION, on its own.
 *
 * The strip block also carries `items` and `warmLabels`, which belong to the
 * coins and to nothing else. A caller that wants the same MOVEMENT — the gamble
 * scatter does, so the two stop reading as different features side by side —
 * takes this and supplies its own contents.
 *
 * Derived from the config rather than written out again: re-tune a coin and
 * everything borrowing its motion re-tunes with it. These numbers must never
 * exist in two places.
 */
export const goldenStripMotion = () => {
  const {
    speedMs,
    accelMs,
    overshoot,
    landingExitSpeed,
    windUpDistance,
    windUpMs,
  } = buildGoldenMysteryConfig().strip;
  return {
    speedMs,
    accelMs,
    overshoot,
    landingExitSpeed,
    windUpDistance,
    windUpMs,
  };
};

export default buildGoldenMysteryConfig;
